import { formatCurrency, formatDate } from '../fomatter.utils'
import { BODY_FONT, splitLines, wrapText } from '../text.utils'
import type { TicketElement } from '../TicketElement'
import type { DoubleInfoItemOptions } from '../types'

export class DoubleInfoItem implements TicketElement {
  firstLabel: string
  firstValue: string | number | undefined | null
  secondLabel: string
  secondValue: string | number | undefined | null
  secondType: 'string' | 'date' | 'currency'
  firstLabelFont: string
  secondLabelFont: string
  font: string
  lineHeight: number
  important?: boolean

  constructor(
    firstLabel: string,
    firstValue: string | number | undefined,
    secondLabel: string,
    secondValue: string | number | undefined,
    { firstLabelFont, secondLabelFont, secondType }: DoubleInfoItemOptions = {}
  ) {
    this.firstLabel = firstLabel + ':'
    this.firstValue = firstValue
    this.secondLabel = secondLabel + ':'
    this.secondValue = secondValue
    this.secondType = secondType || 'string'
    this.firstLabelFont = firstLabelFont || `bold 16px ${BODY_FONT}`
    this.secondLabelFont = secondLabelFont || `bold 16px ${BODY_FONT}`
    this.font = `16px ${BODY_FONT}`
    this.lineHeight = 25
    if (this.secondType === 'date') {
      this.secondValue = formatDate(this.secondValue)
    } else if (this.secondType === 'currency') {
      this.secondValue = formatCurrency(+this.secondValue!)
    }
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    if (!this.firstValue && !this.secondValue) {
      return 0
    }
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    ctx.font = this.firstLabelFont
    const fullFirstValue = this.firstLabel + ' ' + this.firstValue + ' '
    const fullSecondValue = this.secondLabel + ' ' + this.secondValue
    if (splitLines(ctx, fullFirstValue + fullSecondValue, width).length === 1) {
      // Both first item and second item fit in a line, total height will be one line
      return this.lineHeight * 1
    } else {
      // First and second items don't fit in one line, so total lines is the sum of the lines of each
      return (
        this.lineHeight *
        (splitLines(ctx, fullFirstValue, width).length +
          splitLines(ctx, fullSecondValue, width).length)
      )
    }
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (!this.firstValue && !this.secondValue) return
    top += this.lineHeight
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    const fullFirstValue = this.firstLabel + ' ' + this.firstValue + ' '
    const fullSecondValue = this.secondLabel + ' ' + this.secondValue
    ctx.font = this.firstLabelFont
    const bothItemsDontFitInOneLine =
      splitLines(ctx, fullFirstValue + fullSecondValue, width).length > 1
    if (bothItemsDontFitInOneLine) {
      ctx.fillText(this.firstLabel, 0, top)
      ctx.font = this.font
      const firstItemLines = wrapText(
        ctx,
        this.firstValue!,
        ctx.measureText(this.firstLabel).width + 5,
        top,
        width - (ctx.measureText(this.firstLabel).width + 10),
        {
          lineHeight: this.lineHeight,
          decreaseIndent: true
        }
      )
      ctx.font = this.secondLabelFont
      ctx.fillText(this.secondLabel, 0, top + this.lineHeight * firstItemLines)
      ctx.font = this.font
      wrapText(
        ctx,
        this.secondValue!,
        ctx.measureText(this.secondLabel).width + 5,
        top + this.lineHeight * firstItemLines,
        width - (ctx.measureText(this.secondLabel).width + 10),
        {
          lineHeight: this.lineHeight,
          decreaseIndent: true
        }
      )
    } else {
      if (this.firstValue) {
        ctx.font = this.firstLabelFont
        ctx.fillText(this.firstLabel, 0, top)
        ctx.font = this.font
        ctx.fillText(this.firstValue.toString(), ctx.measureText(this.firstLabel).width + 10, top)
        if (this.secondValue) {
          ctx.textAlign = 'right'
          ctx.fillText(this.secondValue.toString(), width, top)
          ctx.font = this.secondLabelFont
          ctx.fillText(
            this.secondLabel,
            width - (ctx.measureText(this.secondValue.toString()).width + 10),
            top
          )
        }
      } else {
        ctx.font = this.secondLabelFont
        ctx.fillText(this.secondLabel, 0, top)
        ctx.font = this.font
        ctx.fillText(
          this.secondValue!.toString(),
          ctx.measureText(this.secondLabel).width + 10,
          top
        )
      }
    }

    if (this.important) {
      ctx.font = `bold 18px ${BODY_FONT}`
    }
  }
}
